import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  PagedRouters, RouterCreateInput,
  RouterDetail, RouterUpdateInput, TestConnectionResponse,
} from "./schemas";

const KEY = "routers";

export function useListRouters(filters: { page?: number; size?: number; search?: string }) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => {
      const p: Record<string, string | number> = { page: filters.page ?? 1, size: filters.size ?? 20 };
      if (filters.search) p.search = filters.search;
      return apiCall<PagedRouters>("get", "/api/v1/routers", { searchParams: p });
    },
    placeholderData: (prev) => prev,
  });
}

export function useRouter(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<RouterDetail>("get", `/api/v1/routers/${id}`),
    enabled: !!id,
  });
}

export function useCreateRouter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RouterCreateInput) => apiCall<RouterDetail>("post", "/api/v1/routers", { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdateRouter(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RouterUpdateInput) => apiCall<RouterDetail>("patch", `/api/v1/routers/${id}`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteRouter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiCall<{ message: string }>("delete", `/api/v1/routers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<TestConnectionResponse>("post", `/api/v1/routers/${id}/test-connection`),
  });
}

/** F15/F16: Encola recreación de reglas de firewall (suspend-drop). */
export function useRecrearReglas() {
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ job_id: string }>(
        "post", `/api/v1/routers/${id}/recrear-reglas`, { json: {} },
      ),
  });
}

/** F15.11/F16: Encola reconciliación completa del router. */
export function useReconciliarTodo() {
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ job_id: string }>(
        "post", `/api/v1/routers/${id}/reconciliar-todo`, { json: {} },
      ),
  });
}

// F24.3: OVPN movido a feature /vpn — ver web/src/features/vpn/api.ts
